/**
 * Pure helpers for the ALL-products (releases "What's New") view.
 *
 * Two directions, both framework-free:
 *   - materializeAllProductsWhatsNewFields — read the ALL editor's per-product
 *     scoped keys back onto a single product's canonical keys.
 *   - aggregateAllProductsFromScenes — fan a per-product scene map out into one
 *     scene whose per-template keys are scoped as "{TemplateId}-{ProductCode}".
 *
 * Extracted from useInitialScene so (a) the v3 default-scene resolver can hold a
 * server-side copy of the materialization with a parity test against this
 * original (see src/server/api/v3/resolve-defaults.ts), and (b) the aggregation
 * can run over pre-merged scenes from either the client cascade or
 * GET /api/v3/default-scenes/resolved-all.
 */

export const ALL_PRODUCTS_TITLE_KEY_PREFIX = "all-products-whats-new-title:";
export const ALL_PRODUCTS_FEATURES_KEY_PREFIX = "all-products-whats-new-features:";
export const ALL_PRODUCTS_BG_OPTIONS_FIELD = "allProductsBgOptions";
export const WHATS_NEW_TEMPLATE_ID = "WhatsNewPreview";
export const WHATS_NEW_BG_FIELDS = ["bgX", "bgY", "bgScale", "bgRotate", "bgFlip"];

/**
 * Per-template object fields that store values keyed by template ID.
 * In ALL-products view, each product/template instance gets a unique scoped ID
 * ("{TemplateId}-{ProductCode}") and values are remapped accordingly.
 */
export const PER_TEMPLATE_OBJECT_FIELDS = [
  "titles",
  "features",
  "subtitles",
  "subtitles2",
  "subtitles3",
  "names",
  "dates",
  "badges",
  "links",
  "links2",
  "links3",
  "versions",
  "eaps",
  "positions",
  "productNames",
  "bgX",
  "bgY",
  "bgScale",
  "bgRotate",
  "bgFlip",
  "uploadX",
  "uploadY",
  "uploadScale",
  "uploadRotate",
  "uploadFlip",
  "logoX",
  "logoY",
  "logoScale",
  "uploadImages",
  "logos",
];

/**
 * The look a background gives one banner (libs/backgroundAppearance.ts): the
 * banner's own image (a ready cut), its base colour, its fade, and the colours
 * of its text, logo and button. In the ALL view each banner is the banner of
 * one product's own page, so it takes these from that product's scene. A
 * `scalar` is the value of every banner of the product without a value of its
 * own.
 */
export const INSTANCE_APPEARANCE_FIELDS = Object.freeze([
  {field: "customBgs"},
  {field: "bases", scalar: "base"},
  {field: "bgOpacities", scalar: "bgOpacity"},
  {field: "bgBrightnesses", scalar: "bgBrightness"},
  {field: "textColorLocked"},
  {field: "logoVariants"},
  {field: "buttonColors"},
]);

/**
 * `{field: {"<TemplateId>-<code>": value}}`: the look `scene` (the scene of
 * product `code`) gives each of `templateIds`, keyed by its ALL-view instance.
 * A field the scene gives a template no value for has no key for it.
 */
export function productInstanceAppearance(scene, code, templateIds) {
  const out = {};
  for (const {field, scalar} of INSTANCE_APPEARANCE_FIELDS) {
    const record = scene?.[field];
    const shared = scalar ? scene?.[scalar] : undefined;
    for (const templateId of templateIds) {
      const own = record && typeof record === "object" ? record[templateId] : undefined;
      const value = own ?? shared;
      if (value === undefined || value === null) continue;
      out[field] = out[field] || {};
      out[field][`${templateId}-${code}`] = value;
    }
  }
  return out;
}

/**
 * `scene` without the look of the ALL view's banners
 * (INSTANCE_APPEARANCE_FIELDS keys of `<TemplateId>-<code>` instances). Each
 * of them shows the look its product's rows give it. A copy saved into the ALL
 * row would hide every later change of those rows, such as a new ready cut.
 */
export function withoutInstanceAppearance(scene, productCodes) {
  const byLongest = [...productCodes].sort((a, b) => b.length - a.length);
  const isInstance = (id) =>
    byLongest.some((code) => id.length > code.length + 1 && id.endsWith(`-${code}`));
  let out = scene;
  for (const {field} of INSTANCE_APPEARANCE_FIELDS) {
    const record = scene?.[field];
    if (!record || typeof record !== "object") continue;
    const kept = Object.fromEntries(Object.entries(record).filter(([id]) => !isInstance(id)));
    if (Object.keys(kept).length === Object.keys(record).length) continue;
    if (out === scene) out = {...scene};
    out[field] = kept;
  }
  return out;
}

/**
 * Read the ALL-products editor's per-product scoped keys onto `productCode`'s
 * canonical keys, so a single-product route shows what was saved in ALL mode:
 * `all-products-whats-new-title:IJ` → `titles.main`, `allProductsBgOptions.IJ`
 * → `bgOption`, `bgX["WhatsNewPreview-IJ"]` → `bgX.WhatsNewPreview`.
 *
 * Applies to ONE cascade layer at a time: a more specific layer's canonical
 * value must be able to win over a less specific layer's scoped value, which is
 * only true if materialization happens before the merge.
 */
export function materializeAllProductsWhatsNewFields(scene, productCode) {
  if (!scene || !productCode || productCode === "ALL") return scene;

  const titleKey = `${ALL_PRODUCTS_TITLE_KEY_PREFIX}${productCode}`;
  const featureKey = `${ALL_PRODUCTS_FEATURES_KEY_PREFIX}${productCode}`;
  const titleOverride = scene?.titles?.[titleKey];
  const featureOverride = scene?.features?.[featureKey];
  const next = {...scene};
  let changed = false;
  if (titleOverride !== undefined) {
    next.titles = {...(scene.titles || {}), main: titleOverride};
    changed = true;
  }
  if (featureOverride !== undefined) {
    next.features = {...(scene.features || {}), main: featureOverride};
    changed = true;
  }
  const bgOptionOverride = scene?.[ALL_PRODUCTS_BG_OPTIONS_FIELD]?.[productCode];
  if (bgOptionOverride !== undefined) {
    next.bgOption = String(bgOptionOverride);
    changed = true;
  }

  // ALL-products editor stores per-product bg transforms in scoped keys
  // (e.g. WhatsNewPreview-IJ). Product routes read canonical key
  // (WhatsNewPreview), so materialize it here.
  const scopedTemplateId = `${WHATS_NEW_TEMPLATE_ID}-${productCode}`;
  for (const field of WHATS_NEW_BG_FIELDS) {
    const fieldValue = scene?.[field];
    const scopedValue = fieldValue?.[scopedTemplateId];
    if (scopedValue !== undefined) {
      next[field] = {
        ...(fieldValue || {}),
        [WHATS_NEW_TEMPLATE_ID]: scopedValue,
      };
      changed = true;
    }
  }

  return changed ? next : scene;
}

/**
 * For the ALL products view: given each product's already-merged default scene,
 * build a scene-like object where per-template fields are remapped from
 * "{TemplateId}" → "{TemplateId}-{ProductCode}".
 *
 * Titles/features are remapped as normal per-template fields and additionally
 * keep backward-compatible all-products-whats-new-* keys. Each banner also
 * takes the look of its product's background (productInstanceAppearance).
 *
 * The result can be passed to mergeScenes as the least-specific layer;
 * global override layers on top will take priority for conflicting keys.
 *
 * @param {Record<string, object|null>} scenesByProduct product code → merged scene
 * @param {string[]} productCodes products to fan out (order irrelevant)
 * @param {string[]} templateIds template ids of the current set
 * @param {string[]} [extraTemplateIds] banners an admin added from other sets
 *   (libs/bannerComposition.ts): fanned out the same way, but only the set's
 *   own lone What's New banner takes a product's shared `main` values.
 */
export function aggregateAllProductsFromScenes(
  scenesByProduct,
  productCodes,
  templateIds,
  extraTemplateIds = []
) {
  const scene = {};
  const setTemplateIds =
    Array.isArray(templateIds) && templateIds.length > 0 ? templateIds : [WHATS_NEW_TEMPLATE_ID];
  const scopedTemplateIds = [
    ...setTemplateIds,
    ...extraTemplateIds.filter((id) => !setTemplateIds.includes(id)),
  ];

  for (const code of productCodes) {
    const sd = scenesByProduct?.[code];
    if (!sd) continue;

    for (const templateId of scopedTemplateIds) {
      const dstId = `${templateId}-${code}`;

      for (const field of PER_TEMPLATE_OBJECT_FIELDS) {
        const valueByTemplate = sd[field];
        if (!valueByTemplate || typeof valueByTemplate !== "object") continue;

        const valueFromTemplate = valueByTemplate[templateId];
        const fallbackMain =
          setTemplateIds.length === 1 && templateId === WHATS_NEW_TEMPLATE_ID
            ? valueByTemplate.main
            : undefined;
        const value = valueFromTemplate !== undefined ? valueFromTemplate : fallbackMain;
        if (value === undefined) continue;

        scene[field] = scene[field] || {};
        scene[field][dstId] = value;
      }
    }

    for (const [field, byInstance] of Object.entries(
      productInstanceAppearance(sd, code, scopedTemplateIds)
    )) {
      scene[field] = {...scene[field], ...byInstance};
    }

    // Backward compatibility: ALL-products whats-new editor stores title/feature
    // in special per-product keys (all-products-whats-new-...).
    const title = sd.titles?.[WHATS_NEW_TEMPLATE_ID] ?? sd.titles?.main;
    if (title !== undefined) {
      scene.titles = scene.titles || {};
      scene.titles[`${ALL_PRODUCTS_TITLE_KEY_PREFIX}${code}`] = title;
    }

    const feature = sd.features?.[WHATS_NEW_TEMPLATE_ID] ?? sd.features?.main;
    if (feature !== undefined) {
      scene.features = scene.features || {};
      scene.features[`${ALL_PRODUCTS_FEATURES_KEY_PREFIX}${code}`] = feature;
    }
  }

  return scene;
}
